/**
 * extractError — normaliza errores de axios/FastAPI a string seguro para toast.
 *
 * Por qué existe: Pydantic v2 devuelve `detail` como un array de objetos
 * ({loc, msg, type}). Pasarlo directamente a `toast.error()` hace que React
 * intente renderizar un objeto → crash. Este helper siempre devuelve un string.
 *
 * Casos manejados:
 *   1. Error de red (sin respuesta): mensaje de axios (e.message)
 *   2. detail string  → devuelve tal cual
 *   3. detail array (Pydantic v2) → une los `msg` de cada item
 *   4. detail objeto  → JSON.stringify o fallback
 *   5. Sin detail pero hay data  → JSON.stringify del data completo
 *   6. Fallback al mensaje de axios
 */
export function extractError(err, fallback = "An unexpected error occurred") {
  if (!err) return fallback;

  const detail = err?.response?.data?.detail;

  if (detail !== undefined && detail !== null) {
    if (typeof detail === "string") return detail;

    if (Array.isArray(detail)) {
      // Pydantic v2: [{loc, msg, type}, ...]
      const messages = detail
        .map((item) => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object") {
            // loc puede ser ["body","max_tokens"] → tomamos el último segmento
            const field = Array.isArray(item.loc) ? item.loc[item.loc.length - 1] : null;
            const msg = item.msg || item.message || JSON.stringify(item);
            return field ? `${field}: ${msg}` : msg;
          }
          return String(item);
        })
        .filter(Boolean)
        .join("; ");
      return messages || fallback;
    }

    if (typeof detail === "object") {
      // Objeto arbitrario — serializa sin crashear React
      try {
        return JSON.stringify(detail);
      } catch {
        return fallback;
      }
    }

    return String(detail);
  }

  // Sin detail: prueba con el mensaje de axios / fetch
  if (err.message) return err.message;

  return fallback;
}
